import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { HERO } from "../data/content";

const lineEase = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-[#F8F7F4]">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          src={HERO.image}
          alt={HERO.imageAlt}
          fetchPriority="high"
          decoding="async"
          initial={{ scale: 1.15, filter: "blur(6px)" }}
          animate={{ scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 2, ease: lineEase }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F8F7F4] via-[#F8F7F4]/40 to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-20 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-xs uppercase tracking-[0.3em] font-bold text-[#B85C38] mb-6"
          data-testid="hero-overline"
        >
          {HERO.overline}
        </motion.p>

        <h1 className="font-serif font-light text-[#16281D] text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] tracking-tighter" data-testid="hero-title">
          {HERO.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.4, delay: 0.4 + i * 0.12, ease: lineEase }}
              >
                {i === 2 ? <em className="italic text-[#B85C38]">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.1 }}
            className="max-w-md text-base md:text-lg text-[#4A5D52] leading-relaxed"
            data-testid="hero-subtitle"
          >
            {HERO.sub}
          </motion.p>
          <motion.a
            href="#hizmetler"
            data-testid="hero-scroll-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-bold text-[#16281D]"
          >
            Keşfedin
            <span className="w-10 h-10 rounded-full border border-[#16281D]/30 flex items-center justify-center group-hover:bg-[#16281D] group-hover:text-[#F8F7F4] transition-colors duration-300">
              <ArrowDown size={16} className="animate-bounce" />
            </span>
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};
